import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../components/hooks/useTableSortInit';
import SummaryTiles, { TILE_ACCENT } from '../../../../components/utils/SummaryTiles';
import TableTimeStamps from '../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../components/utils/Copyable';
import TimeAgo from '../../../../components/utils/TimeAgo';
import TopBar from '../../../../components/utils/TopBar';
import NA from '../../../../components/utils/NA';

import {
  GetInternalUsersMfaBIZidentity
} from '../../../../redux/actions/business/identity/internalUsersMfaActions';

const InternalUsersMfaPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.identity"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [mfaList, setMfaList] = useState([]);
  const [mfaTotal, setMfaTotal] = useState(0);
  const [summary, setSummary] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "id",
    },
    {
      label: "Internal user",
      sort: "internal_user_id",
    },
    {
      label: "Method",
      sort: "method",
    },
    {
      label: "Status",
      sort: "is_enabled",
    },
    {
      label: "Primary",
      sort: "is_primary",
    },
    {
      label: "Last used",
      sort: "last_used_at",
    },
    {
      label: "Time stamps",
      sort: "createdAt",
    }
  ];

  useEffect(() => {
    handleGetMfa();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setMfaList(state.internalUsersMfaList?.records || []);
      setMfaTotal(state.internalUsersMfaList?.total || 0);
      setSummary(state.internalUsersMfaList?.summary || null);
    } catch (err) {}
  }, [state]);

  async function handleGetMfa(customLoading){
    let query = "";
    if(search) query += `&search=${search}`;
    if(sort) query += `&field=${sort}`;
    if(sortDirection) query += `&order=${sortDirection}`;
    await dispatch(GetInternalUsersMfaBIZidentity(page+1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  const summaryTiles = [
    { label: "MFA enrolments", value: Number(summary?.total || 0), accent: TILE_ACCENT.neutral },
    { label: "Enabled", value: Number(summary?.enabled || 0), accent: TILE_ACCENT.good },
    { label: "Not enabled", value: Number(summary?.disabled || 0), accent: TILE_ACCENT.warn },
  ];

  return (
    <Box>

      <TopBar
        title={"Internal Users MFA"}
        search={search}
        searchPlaceholder={"Search id, internal user id or method"}
        onSearchChange={handleSearchChange}
        button1Text={"Refresh"}
        button1OnClick={() => {
          handleGetMfa();
        }}
        />

      <SummaryTiles tiles={summaryTiles} />

      <Typography
        variant='caption'
        sx={{ color: "grey", display: "block", mb: "14px" }}>
        Enrolment records only. The TOTP secret and the anti-replay counter are
        never returned by the API, so they cannot be read from this screen.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          mfaList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No MFA enrolment found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                mfaList.map((row, index) => (
                  <TableRow
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography>
                          {row?.internal_user?.first_name || <NA />}&nbsp;{row?.internal_user?.last_name || ""}
                        </Typography>
                        <i><Typography variant='caption'>{row?.internal_user?.primary_email || <NA />}</Typography></i>
                      </Box>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.internal_user_id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.method || <NA />}</TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        label={row.is_enabled ? "Enabled" : "Pending"}
                        color={row.is_enabled ? "success" : "warning"}
                        variant={row.is_enabled ? "filled" : "outlined"}
                        />
                    </TableCell>
                    <TableCell>
                      {
                        row.is_primary
                        ? <Chip size='small' label="Primary" color="info" variant='outlined' />
                        : <NA />
                      }
                    </TableCell>
                    <TableCell>
                      {
                        row.last_used_at
                        ? <TimeAgo value={row.last_used_at} />
                        : <Typography variant='body2' color={"grey"}>Never used</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.createdAt}
                        updatedAt={row.updatedAt}
                        />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={mfaTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
    </Box>
  )
}

export default InternalUsersMfaPage;
